import type { GetThunkAPI, PayloadAction } from '@reduxjs/toolkit';
import { createAsyncThunk, createSlice } from '@reduxjs/toolkit';
import * as Sentry from '@sentry/react';
import { DateTime } from 'luxon';
import * as platform from '../../shared/helpers/platformApi';
import { finaliseLogout } from '../../shared/store/thunks';
import appConfig from '../helpers/appConfig';
import type { SearchContactResultItem } from '../helpers/partnerCrm';
import type { StoreDispatch, StoreState, ThunkAPI } from '../store/types';

const getPartnerCrm = () => import('../helpers/partnerCrm');

interface CreateCall {
  id: string;
  // FIXME phonebookentry etc?
  type?: string;
  direction: 'IN' | 'OUT';
  number: string;
  name?: string;
  description?: string;
  email?: string;
  tags?: string | string[];
}

interface Call extends CreateCall {
  tags: string[];
  onHold: boolean;
  muted: boolean;
  active: boolean;
  answered: boolean;
  answering: boolean;
  ringing: boolean;
  ending: boolean;
  transferInitiated: boolean;
  isTransferring: boolean;
  forceStopRinging: boolean;
  diallerVisible: boolean;
  tones: string[];
  created: number;
  started?: number;
  crmDetails?: SearchContactResultItem[];
}

type CallsState = Call[];

let allowNewCall = true;

const populateCrmContact = (
  number: string,
  sessionId: string,
  thunkAPI: GetThunkAPI<{ state: StoreState; dispatch: StoreDispatch }>,
) => {
  const {
    vcrm: { isRegistered },
  } = thunkAPI.getState();
  if (!isRegistered || number.length < 5) {
    return;
  }

  getPartnerCrm()
    .then((partnerCrm) => partnerCrm.searchContact(number))
    .then((res) => {
      if (res.length <= 0) {
        return;
      }
      thunkAPI.dispatch(setCrmDetails({ id: sessionId, crmDetails: res }));
    })
    .catch((err) => {
      console.error(err);
    });
};

const logCrmActivity = (
  callId: string,
  thunkAPI: GetThunkAPI<{ state: StoreState; dispatch: StoreDispatch }>,
) => {
  const {
    vcrm: { isRegistered },
    calls,
  } = thunkAPI.getState();
  const call = calls.find((c) => c.id === callId);
  // TODO do we log calls between extensions?
  if (
    !isRegistered ||
    !call ||
    call.number.length < 5 ||
    !call.crmDetails?.length
  ) {
    return;
  }
  const callStarted = call?.started || Date.now();
  const duration = Math.floor((Date.now() - callStarted) / 1000);
  const body: Record<string, any> = {
    callId: callId,
    direction: call.direction === 'IN' ? 'Incoming' : 'Outgoing',
    duration,
    start: DateTime.fromMillis(callStarted).toFormat("yyyy-MM-dd'T'HH:mm:ss"),
  };

  if (call.direction === 'IN') {
    body.calledTel = call.number;
    body.calledName = call.name;
  } else {
    body.callerTel = call.number;
    body.callerName = call.name;
  }

  call.crmDetails.forEach((crmd) => {
    const record = crmd.records.find((r) => !!r.createPhoneActivityActionId);
    if (!record) {
      return;
    }
    getPartnerCrm()
      .then((partnerCrm) =>
        partnerCrm.doAction(record.createPhoneActivityActionId, body),
      )
      .catch((err) => {
        console.error(err);
      });
  });
};

export const startCall = createAsyncThunk<
  void,
  {
    number: string;
    type?: string;
    name?: string;
    description?: string;
    email?: string;
  },
  ThunkAPI
>('calls/startCall', async (params, thunkAPI) => {
  const { number, type, name, description, email } = params;
  // If the allowNewCall flag is false, don't allow a call to be started
  if (!allowNewCall) return;

  setTimeout(() => {
    allowNewCall = true;
  }, 1200);

  const { extension } = appConfig;
  const { calls, client } = thunkAPI.getState();

  if (!extension) {
    return;
  }

  const ringingOutobundCall = calls.find(
    (c) => c.ringing && c.direction === 'OUT',
  );

  // If there's a call ringing, don't allow another call to be started
  if (ringingOutobundCall) return;

  const { audio: audioState } = thunkAPI.getState();
  const inputDeviceId = audioState.selectedAudioInputDeviceId;
  const audioConstraint =
    inputDeviceId && inputDeviceId !== 'default'
      ? { deviceId: { exact: inputDeviceId } }
      : true;

  const outboundCallOptions = {
    mediaConstraints: {
      audio: audioConstraint,
      video: false,
    },
    rtcOfferConstraints: {
      offerToReceiveVideo: 0,
    },
    extraHeaders: [] as string[],
  };

  const callerId = client.selectedCallerId;

  if (callerId?.number) {
    outboundCallOptions.extraHeaders.push(
      `P-Preferred-Identity: <sip:${callerId.number.replace(
        /\s/g,
        '',
      )}@sipcentric.com>`,
    );
  }

  const call = extension.dial(number, outboundCallOptions);

  const activeCallState = calls.find((c) => c.active);

  const callInfo = {
    type,
    number,
    name,
    description,
    email,
    direction: 'OUT' as const,
  };

  // Set up event handlers
  call.on?.('connecting', () => {
    thunkAPI.dispatch(createCall({ id: call.session.id, ...callInfo }));
    populateCrmContact(number, call.session.id, thunkAPI);
  });
  call.on?.('progress', (e: any) => {
    // TODO: stop on any 1xx with SDP? First check carriers don't send 180+SDP with no RTP
    if (e.response && e.response.status_code === 183) {
      thunkAPI.dispatch(callRingingStop({ id: call.session.id }));
    }
    thunkAPI.dispatch(callRinging({ id: call.session.id }));
  });
  call.on?.('accepted', () => {
    thunkAPI.dispatch(callAnswered({ id: call.session.id }));
  });
  call.on?.('ended', () => {
    logCrmActivity(call.session.id, thunkAPI);
    thunkAPI.dispatch(callRemoved({ id: call.session.id }));
  });
  call.on?.('failed', (err: any) => {
    console.error('Call failed', err);
    thunkAPI.dispatch(callRemoved({ id: call.session.id }));
  });
  call.on?.('hold', () => thunkAPI.dispatch(callHold({ id: call.session.id })));
  call.on?.('unhold', () =>
    thunkAPI.dispatch(callUnhold({ id: call.session.id })),
  );
  call.on?.('muted', () =>
    thunkAPI.dispatch(callMuted({ id: call.session.id })),
  );
  call.on?.('unmuted', () =>
    thunkAPI.dispatch(callUnmuted({ id: call.session.id })),
  );
  call.on?.('refersucceeded', () =>
    thunkAPI.dispatch(callRemoved({ id: call.session.id })),
  );
  call.on?.('referfailed', () =>
    thunkAPI.dispatch(cancelTransfer({ id: call.session.id })),
  );
  call.on?.('newDTMF', (e: any) => {
    const { tone } = e.dtmf;
    if (e.originator === 'local') {
      thunkAPI.dispatch(sentDTMF({ id: call.session.id, tone }));
    }
  });

  allowNewCall = false;

  if (activeCallState) {
    const activeCallId = activeCallState.id;
    const activeCall = extension.getOngoingCall(activeCallId);

    if (!activeCall.isOnHold) {
      activeCall.once('hold', () => {
        thunkAPI.dispatch(callDeactivate({ id: activeCallId }));
        call.invite();
      });

      // thunkAPI.dispatch(holdingCall({id:activeCallId}));
      activeCall.hold();
    } else {
      thunkAPI.dispatch(callDeactivate({ id: activeCallId }));
      call.invite();
    }
  } else {
    call.invite();
  }
});

export const endCall = createAsyncThunk<void, string, ThunkAPI>(
  'calls/endCall',
  async (id, thunkAPI) => {
    const { extension } = appConfig;
    if (!extension) {
      return;
    }

    const call = extension.getOngoingCall(id);
    thunkAPI.dispatch(endingCall({ id }));
    if (!call) {
      console.warn(`Ongoing call with id ${id} doesn't exist`);
      Sentry.captureException(
        new Error(`Ongoing call doesn't exist (trying to end)`),
      );
      // end the call not to leave the user with unresponsive UI
      thunkAPI.dispatch(callRemoved({ id }));
    } else {
      call.bye();
    }
    try {
      platform.closeNotification(id);
    } catch (err) {
      console.debug('Error closing notification: ', err);
    }
  },
);

// FIXME doesnt have to be thunk, maybe also others here?
export const holdCall = createAsyncThunk<void, string, ThunkAPI>(
  'calls/holdCall',
  async (id) => {
    const { extension } = appConfig;
    if (!extension) {
      return;
    }
    const call = extension.getOngoingCall(id);
    // thunkAPI.dispatch(holdingCall({id}));
    call.hold();
  },
);

export const holdCallAndDeactivate = createAsyncThunk<void, string, ThunkAPI>(
  'calls/holdCallAndDeactivate',
  async (id, thunkAPI) => {
    const { extension } = appConfig;
    if (!extension) {
      return;
    }
    const call = extension.getOngoingCall(id);
    if (call.isOnHold) {
      thunkAPI.dispatch(callDeactivate({ id: call.session.id }));
    } else {
      // thunkAPI.dispatch(holdingCall({id}));
      call.once('hold', () =>
        thunkAPI.dispatch(callDeactivate({ id: call.session.id })),
      );
      call.hold();
    }
  },
);

export const unholdCall = createAsyncThunk<void, string, ThunkAPI>(
  'calls/unholdCall',
  async (id, thunkAPI) => {
    const { extension } = appConfig;
    if (!extension) {
      return;
    }
    const call = extension.getOngoingCall(id);
    const { calls } = thunkAPI.getState();
    const callToUnhold = calls.find((c) => c.id === id);
    const activeCall = calls.find((c) => c.active);

    // If the call we're unholding is the active call
    // or there isn't an active call then that's
    // easy, just unhold it.
    // Otherwise, we need to also remember to
    // deactivate the active call as we unhold

    if (callToUnhold?.active || !activeCall) {
      // thunkAPI.dispatch(unholdingCall({id}));
      call.unhold();
    } else {
      // thunkAPI.dispatch(unholdingCall({id}));
      call.once('unhold', () =>
        thunkAPI.dispatch(callDeactivate({ id: activeCall.id })),
      );
      call.unhold();
    }
  },
);

export const muteCall = createAsyncThunk<void, string, ThunkAPI>(
  'calls/muteCall',
  async (id) => {
    const { extension } = appConfig;
    if (!extension) {
      return;
    }
    const call = extension.getOngoingCall(id);
    // thunkAPI.dispatch(mutingCall({id}));
    call.mute();
  },
);

export const unmuteCall = createAsyncThunk<void, string, ThunkAPI>(
  'calls/unmuteCall',
  async (id) => {
    const { extension } = appConfig;
    if (!extension) {
      return;
    }
    const call = extension.getOngoingCall(id);
    // thunkAPI.dispatch(unmutingCall({id}));
    call.unmute();
  },
);

export const transferCall = createAsyncThunk<
  void,
  { id?: string; number: string },
  ThunkAPI
>('calls/transferCall', async (params, thunkAPI) => {
  const { extension } = appConfig;
  if (!extension || !params.id) {
    return;
  }
  const call = extension.getOngoingCall(params.id);
  thunkAPI.dispatch(transferringCall({ id: params.id }));
  call.refer(params.number);
});

export const attendedTransferCall = createAsyncThunk<void, string, ThunkAPI>(
  'calls/attendedTransferCall',
  async (id, thunkAPI) => {
    const { extension } = appConfig;
    if (!extension) {
      return;
    }
    const activeCall = extension.getActiveCall();
    const callToTransfer = extension.getOngoingCall(id);
    // Need an established call to transfer to; never REFER a ringing call or itself.
    if (
      !activeCall ||
      !activeCall.answered ||
      !callToTransfer ||
      activeCall.session.id === callToTransfer.session.id
    ) {
      return;
    }
    const activeCallUser = activeCall.session.remote_identity.uri.user;
    const options = {
      replaces: activeCall.session,
    };
    thunkAPI.dispatch(transferringCall({ id: activeCall.session.id }));
    callToTransfer.refer(activeCallUser, options);
  },
);

export const sendDTMF = createAsyncThunk<
  void,
  { id: string; tone: string },
  ThunkAPI
>('calls/sendDTMF', async (params) => {
  const { extension } = appConfig;
  if (!extension) {
    return;
  }
  const call = extension.getOngoingCall(params.id);
  // thunkAPI.dispatch(sendingDTMF({id:params.id}));
  call.sendDTMF(params.tone);
});

export const incomingCall = createAsyncThunk<void, any, ThunkAPI>(
  'calls/incomingCall',
  async (call, thunkAPI) => {
    const { session } = call;

    if (thunkAPI.getState().app.dnd) {
      session.terminate({ status_code: 486, reason_phrase: 'Busy Here' });
      return;
    }

    const callFrom = session._request.from.uri.user;

    // Defaults
    let type = '';
    let number = callFrom;
    let name = callFrom;
    let description = '';
    let email = '';

    const state = thunkAPI.getState();
    const {
      contacts: { phonebook, phones, virtuals },
    } = state;
    const timeout = state.client.extension?.timeout;

    const contactsArr = [...phones, ...virtuals, ...phonebook];

    const contact = contactsArr.find((c) => {
      const contactNumber =
        c.type === 'phonebookentry' ? c.phoneNumber : c.shortNumber;
      return contactNumber === callFrom;
    });

    if (contact) {
      const contactNumber =
        contact.type === 'phonebookentry'
          ? contact.phoneNumber
          : contact.shortNumber;
      type = contact.type;
      number = contactNumber;
      name = contact.name;
      description = contactNumber;
      // @ts-expect-error
      email = contact.email;
    }

    try {
      const { favicons } = thunkAPI.getState().branding;
      // const x16 = (favicons || []).find(
      //   (fi) => fi.name.toLowerCase() === 'favicon-16x16.png',
      // )?.location;
      const x32 = (favicons || []).find(
        (fi) => fi.name.toLowerCase() === 'favicon-32x32.png',
      )?.location;

      platform.notify(session.id, `Incoming call from ${callFrom}`, '', {
        icon: x32,
        timeout,
      });
    } catch (err) {
      console.debug('Error sending notification: ', err);
    }

    const callInfo = {
      type,
      number,
      name,
      description,
      email,
      direction: 'IN' as const,
      tags: call.tags,
    };

    // Create call
    thunkAPI.dispatch(createCall({ id: session.id, ...callInfo }));

    const endSession = () => {
      logCrmActivity(
        session.id,

        thunkAPI,
      );
      try {
        platform.closeNotification(session.id);
      } catch (err) {
        console.debug('Error closing notification: ', err);
      }

      thunkAPI.dispatch(callRemoved({ id: session.id }));
    };

    populateCrmContact(number, session.id, thunkAPI);

    session.on('progress', () => {
      thunkAPI.dispatch(callRinging({ id: session.id }));
    });
    session.on('accepted', () =>
      thunkAPI.dispatch(callAnswered({ id: session.id })),
    );
    session.on('ended', endSession);
    session.on('failed', endSession);
    session.on('hold', () => thunkAPI.dispatch(callHold({ id: session.id })));
    session.on('unhold', () =>
      thunkAPI.dispatch(callUnhold({ id: session.id })),
    );
    session.on('muted', () => thunkAPI.dispatch(callMuted({ id: session.id })));
    session.on('unmuted', () =>
      thunkAPI.dispatch(callUnmuted({ id: session.id })),
    );
    session.on('refersucceeded', () =>
      thunkAPI.dispatch(callRemoved({ id: session.id })),
    );
    session.on('referfailed', () =>
      thunkAPI.dispatch(cancelTransfer({ id: session.id })),
    );
    session.on('newDTMF', (e: any) => {
      const { tone } = e.dtmf;
      if (e.originator === 'local') {
        thunkAPI.dispatch(sentDTMF({ id: session.id, tone }));
      }
    });
  },
);

export const answerCall = createAsyncThunk<void, string, ThunkAPI>(
  'calls/answerCall',
  async (id, thunkAPI) => {
    const { extension } = appConfig;
    if (!extension) {
      return;
    }
    const { calls, audio: audioState } = thunkAPI.getState();
    const inputDeviceId = audioState.selectedAudioInputDeviceId;
    const audioConstraint =
      inputDeviceId && inputDeviceId !== 'default'
        ? { deviceId: { exact: inputDeviceId } }
        : true;
    const answerOptions = {
      mediaConstraints: { audio: audioConstraint, video: false },
    };
    const callState = calls.find((c) => c.id === id);
    const answering = callState?.answering;
    if (answering) {
      return;
    }
    const call = extension.getOngoingCall(id);

    const answer = () => {
      try {
        platform.closeNotification(id);
      } catch (err) {
        console.debug('Error closing notification: ', err);
      }
      if (!call) {
        console.warn(`Ongoing call with id ${id} doesn't exist`);
        Sentry.captureException(
          new Error(`Ongoing call doesn't exist (trying to answer)`),
        );
        // end the call not to leave the user with unresponsive UI
        thunkAPI.dispatch(callRemoved({ id }));
      } else {
        thunkAPI.dispatch(answeringCall({ id }));
        call.connect(answerOptions);
      }
    };

    const activeCallState = calls.find((c) => c.active);

    if (activeCallState) {
      const activeCallId = activeCallState.id;
      const activeCall = extension.getOngoingCall(activeCallId);

      if (!activeCall.isOnHold) {
        activeCall.once('hold', () => {
          thunkAPI.dispatch(callDeactivate({ id: activeCallId }));
          answer();
        });

        // thunkAPI.dispatch(holdingCall({id:activeCallId}));
        activeCall.hold();
      } else {
        thunkAPI.dispatch(callDeactivate({ id: activeCallId }));
        answer();
      }
    } else {
      answer();
    }
  },
);

const modifyCallById = (
  state: CallsState,
  newCall: Partial<Call> & { id: string },
) => {
  const call = state.find((c) => c.id === newCall.id);
  if (!call) {
    return;
  }
  Object.assign(call, newCall);
};

const callsSlice = createSlice({
  name: 'calls',
  initialState: [] as CallsState,
  reducers: {
    createCall: (state, action: PayloadAction<CreateCall>) => {
      const { tags, direction } = action.payload;
      state.push({
        ...action.payload,
        tags: Array.isArray(tags) ? tags : [],
        onHold: false,
        muted: false,
        active: direction === 'OUT',
        answered: false,
        answering: false,
        ringing: false,
        ending: false,
        transferInitiated: false,
        isTransferring: false,
        forceStopRinging: false,
        diallerVisible: false,
        tones: [],
        created: Date.now(),
      });
    },
    callRinging: (state, action: PayloadAction<{ id: string }>) => {
      modifyCallById(state, { id: action.payload.id, ringing: true });
    },
    callRingingStop: (state, action: PayloadAction<{ id: string }>) => {
      modifyCallById(state, { id: action.payload.id, forceStopRinging: true });
    },
    callAnswered: (state, action: PayloadAction<{ id: string }>) => {
      modifyCallById(state, {
        id: action.payload.id,
        ringing: false,
        active: true,
        answered: true,
        answering: false,
        started: Date.now(),
      });
    },
    callHold: (state, action: PayloadAction<{ id: string }>) => {
      modifyCallById(state, {
        id: action.payload.id,
        onHold: true,
        active: true,
      });
    },
    callDeactivate: (state, action: PayloadAction<{ id: string }>) => {
      modifyCallById(state, {
        id: action.payload.id,
        onHold: true,
        active: false,
      });
    },
    callUnhold: (state, action: PayloadAction<{ id: string }>) => {
      modifyCallById(state, {
        id: action.payload.id,
        onHold: false,
        active: true,
      });
    },
    callMuted: (state, action: PayloadAction<{ id: string }>) => {
      modifyCallById(state, { id: action.payload.id, muted: true });
    },
    callUnmuted: (state, action: PayloadAction<{ id: string }>) => {
      modifyCallById(state, { id: action.payload.id, muted: false });
    },
    endingCall: (state, action: PayloadAction<{ id: string }>) => {
      modifyCallById(state, { id: action.payload.id, ending: true });
    },
    answeringCall: (state, action: PayloadAction<{ id: string }>) => {
      modifyCallById(state, { id: action.payload.id, answering: true });
    },
    initiateTransfer: (state, action: PayloadAction<{ id: string }>) => {
      modifyCallById(state, { id: action.payload.id, transferInitiated: true });
    },
    cancelTransfer: (state, action: PayloadAction<{ id: string }>) => {
      modifyCallById(state, {
        id: action.payload.id,
        transferInitiated: false,
      });
    },
    transferringCall: (state, action: PayloadAction<{ id: string }>) => {
      modifyCallById(state, {
        id: action.payload.id,
        isTransferring: true,
        transferInitiated: false,
      });
    },
    sentDTMF: (state, action: PayloadAction<{ id: string; tone: string }>) => {
      const call = state.find((c) => c.id === action.payload.id);
      if (!call) {
        return;
      }
      call.tones.push(action.payload.tone);
    },
    showDialler: (state, action: PayloadAction<{ id: string }>) => {
      modifyCallById(state, { id: action.payload.id, diallerVisible: true });
    },
    hideDialler: (state, action: PayloadAction<{ id: string }>) => {
      modifyCallById(state, { id: action.payload.id, diallerVisible: false });
    },
    setCrmDetails: (
      state,
      action: PayloadAction<{
        id: string;
        crmDetails: SearchContactResultItem[];
      }>,
    ) => {
      modifyCallById(state, {
        id: action.payload.id,
        crmDetails: action.payload.crmDetails,
      });
    },
    callRemoved: (state, action: PayloadAction<{ id: string }>) => {
      return state.filter((s) => s.id !== action.payload.id);
    },
  },
  extraReducers: (builder) => {
    builder.addCase(finaliseLogout.fulfilled, () => []);
  },
});

export const {
  createCall,
  callRinging,
  callRingingStop,
  callAnswered,
  callHold,
  callDeactivate,
  callUnhold,
  callMuted,
  callUnmuted,
  endingCall,
  answeringCall,
  initiateTransfer,
  cancelTransfer,
  transferringCall,
  sentDTMF,
  showDialler,
  hideDialler,
  callRemoved,
  setCrmDetails,
} = callsSlice.actions;

export default callsSlice.reducer;

// case HOLDING_CALL: {
//   return state;
// }
// case UNHOLDING_CALL: {
//   return state;
// }
// case MUTING_CALL: {
//   return state;
// }
// case UNMUTING_CALL: {
//   return state;
// }
// case SENDING_DTMF: {
//   return {
//     ...state,
//   };
// }
